/**
 * 学生的学科分层面板(Jarod 2026-08-27)。
 *
 * 只列"启用了分层"的学科 —— 没配层级的学科不占位。可改的范围由后端
 * 兜底(老师只能改自己教的学科,管理员不限);这里对非本科学科置灰,
 * 让老师看得见别科的分层但改不动。
 */
import { useTranslation } from "react-i18next";

import { useSetStudentLevel, useStudentLevels, useSubjects } from "../../api/school";
import { LEVEL_KEYS, LevelBadge } from "../../components/SubjectLevel";
import { useMyEmployees } from "../workbench/EmployeeSwitcher";

export function StudentLevelsPanel({ studentId }: { studentId: string }): JSX.Element | null {
  const { t } = useTranslation();
  const subjects = useSubjects();
  const levels = useStudentLevels(studentId);
  const setLevel = useSetStudentLevel(studentId);
  const { employees } = useMyEmployees();

  const levelled = (subjects.data ?? []).filter(
    (s) => s.status !== "archived" && (s.levels?.length ?? 0) > 0,
  );
  if (levelled.length === 0) return null;

  const mine = new Set(employees.map((e) => e.subject_id));
  const current = (id: string) =>
    levels.data?.find((l) => l.subject_id === id)?.level ?? "";

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 mb-6">
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-600">
        {t("levelsPanel.title")}
      </h2>
      <p className="mb-4 text-xs text-slate-400">{t("levelsPanel.hint")}</p>
      <div className="flex flex-col gap-2">
        {levelled.map((sub) => {
          const editable = mine.size === 0 || mine.has(sub.id);
          return (
            <div key={sub.id} className="flex items-center gap-3 text-sm">
              <span className="w-24 shrink-0 text-slate-700">{sub.name}</span>
              <select
                value={current(sub.id)}
                disabled={!editable || setLevel.isPending}
                onChange={(e) =>
                  setLevel.mutate({
                    subject_id: sub.id,
                    level: e.target.value || null,
                  })
                }
                className="rounded border border-slate-200 px-2 py-1 text-xs disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">{t("levelsPanel.unset")}</option>
                {LEVEL_KEYS.filter((k) => sub.levels?.includes(k)).map((k) => (
                  <option key={k} value={k}>
                    {t(`levels.${k}`)}
                  </option>
                ))}
              </select>
              <LevelBadge level={current(sub.id)} />
              {!editable && <span className="text-[10px] text-slate-400">{t("levelsPanel.readonly")}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
